/**
 * V15 MIDI 合成器 — Web Audio API 实现
 *
 * 将 parsed MidiFile 调度为 Web Audio API 合成音色。
 * 灵感来自 Web MIDI API 的轻量替代，Ardot 声音美学约束：
 * - 方波/锯齿波/三角波 — "有棱角的音色"
 * - 极少量混响（<0.15 mix）
 * - 频谱分离，每轨明确
 */

import { parseMidi, type MidiFile } from './midi-parser';

// ---- Program → Oscillator Type Map ----
// 基于 General MIDI 规范，映射到 Web Audio 基础波形

interface ProgramConfig {
  oscillatorType: OscillatorType;
  attack: number;
  decay: number;
  sustain: number;
  release: number;
  filterType: BiquadFilterType;
  filterFreq: number;
  filterQ: number;
  gain: number;
}

const PROGRAM_MAP: Record<number, ProgramConfig> = {
  // Piano family (0-7) → Triangle wave, piano-like envelope
  0:  { oscillatorType: 'triangle', attack: 0.01, decay: 0.5, sustain: 0.1, release: 0.3, filterType: 'lowpass', filterFreq: 4000, filterQ: 1, gain: 0.6 },
  1:  { oscillatorType: 'triangle', attack: 0.01, decay: 0.6, sustain: 0.1, release: 0.4, filterType: 'lowpass', filterFreq: 3500, filterQ: 1, gain: 0.55 },
  // Music Box (10) → Sine, bright
  10: { oscillatorType: 'sine',    attack: 0.005, decay: 0.8, sustain: 0.05, release: 0.6, filterType: 'highpass', filterFreq: 300, filterQ: 0.5, gain: 0.5 },
  // Vibraphone (11) → Sine, medium decay
  11: { oscillatorType: 'sine',    attack: 0.01, decay: 1.2, sustain: 0.05, release: 0.8, filterType: 'lowpass', filterFreq: 3000, filterQ: 0.7, gain: 0.5 },
  12: { oscillatorType: 'sine',    attack: 0.01, decay: 1.0, sustain: 0.05, release: 0.7, filterType: 'lowpass', filterFreq: 3500, filterQ: 0.7, gain: 0.5 },
  // Acoustic Guitar (24-25) → Square, plucky
  24: { oscillatorType: 'square',  attack: 0.005, decay: 0.3, sustain: 0.05, release: 0.15, filterType: 'highpass', filterFreq: 150, filterQ: 1.5, gain: 0.4 },
  25: { oscillatorType: 'square',  attack: 0.005, decay: 0.35, sustain: 0.05, release: 0.2, filterType: 'highpass', filterFreq: 150, filterQ: 1.5, gain: 0.4 },
  // Electric Bass (32-39) → Sawtooth, lowpass
  32: { oscillatorType: 'sawtooth', attack: 0.01, decay: 0.4, sustain: 0.2, release: 0.2, filterType: 'lowpass', filterFreq: 400, filterQ: 2, gain: 0.5 },
  33: { oscillatorType: 'sawtooth', attack: 0.01, decay: 0.3, sustain: 0.2, release: 0.2, filterType: 'lowpass', filterFreq: 350, filterQ: 2, gain: 0.5 },
  34: { oscillatorType: 'sawtooth', attack: 0.01, decay: 0.4, sustain: 0.25, release: 0.25, filterType: 'lowpass', filterFreq: 450, filterQ: 2, gain: 0.5 },
  35: { oscillatorType: 'sawtooth', attack: 0.01, decay: 0.3, sustain: 0.2, release: 0.2, filterType: 'lowpass', filterFreq: 400, filterQ: 2, gain: 0.5 },
  // Lead Synth (80-87) → Sawtooth, bright
  80: { oscillatorType: 'sawtooth', attack: 0.02, decay: 0.3, sustain: 0.3, release: 0.15, filterType: 'lowpass', filterFreq: 6000, filterQ: 1, gain: 0.45 },
  81: { oscillatorType: 'sawtooth', attack: 0.02, decay: 0.25, sustain: 0.3, release: 0.15, filterType: 'lowpass', filterFreq: 7000, filterQ: 1, gain: 0.45 },
  // Pad (88-95) → Triangle, soft
  88: { oscillatorType: 'triangle', attack: 0.1, decay: 0.5, sustain: 0.5, release: 0.4, filterType: 'lowpass', filterFreq: 2000, filterQ: 0.5, gain: 0.35 },
};

function getProgramConfig(program: number): ProgramConfig {
  return PROGRAM_MAP[program] || {
    // Default: triangle wave, neutral envelope
    oscillatorType: 'triangle',
    attack: 0.02,
    decay: 0.3,
    sustain: 0.15,
    release: 0.2,
    filterType: 'lowpass',
    filterFreq: 3000,
    filterQ: 1,
    gain: 0.4,
  };
}

// ---- Note to Frequency ----
function midiToFreq(pitch: number): number {
  return 440 * Math.pow(2, (pitch - 69) / 12);
}

// ---- Main Synth ----

export interface MidiPlaybackHandle {
  stop(): void;
  isPlaying(): boolean;
}

/**
 * Play a MIDI file via Web Audio API synthesis.
 *
 * @param ctx - AudioContext
 * @param dest - Destination node (usually masterGain)
 * @param midiData - Raw MIDI file ArrayBuffer
 * @param options - Playback options
 */
export function playMidi(
  ctx: AudioContext,
  dest: AudioNode,
  midiData: ArrayBuffer,
  options: {
    loop?: boolean;
    volume?: number;      // 0-1, default 0.5
    speed?: number;       // playback speed multiplier, default 1.0
  } = {}
): MidiPlaybackHandle {
  const { loop = false, volume = 0.5, speed = 1.0 } = options;

  let parsed: MidiFile;
  try {
    parsed = parseMidi(midiData);
  } catch (e) {
    console.error('[midi-synth] parse error:', e);
    return { stop: () => {}, isPlaying: () => false };
  }

  const ticksPerBeat = parsed.division;
  const secondsPerTick = 60 / (parsed.tempo * ticksPerBeat) / speed;

  // Per-track gain node for volume control
  const trackGain = ctx.createGain();
  trackGain.gain.value = volume;
  trackGain.connect(dest);

  // Light reverb — Ardot constraint: reverbMix < 0.15
  const dryGain = ctx.createGain();
  dryGain.gain.value = 0.9;
  dryGain.connect(dest);
  
  const wetGain = ctx.createGain();
  wetGain.gain.value = 0.08;

  let reverbNode: ConvolverNode | null = null;
  try {
    reverbNode = ctx.createConvolver();
    reverbNode.buffer = createReverbImpulse(ctx, 0.3);
    reverbNode.connect(wetGain);
    wetGain.connect(dest);
  } catch {
    // Convolver creation failed — skip reverb
    reverbNode = null;
  }

  let playing = true;
  const activeNodes: Array<{ osc: OscillatorNode; gain: GainNode; filter: BiquadFilterNode }> = [];
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let loopTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Schedule all tracks
  let maxEndTime = 0;
  const now = ctx.currentTime + 0.05; // small delay for scheduling

  for (const track of parsed.tracks) {
    const config = getProgramConfig(track.program);

    for (const note of track.notes) {
      const startTime = now + note.startTime * secondsPerTick;
      const duration = note.duration * secondsPerTick;
      const endTime = startTime + duration;

      if (endTime > maxEndTime) maxEndTime = endTime;

      if (endTime <= now) continue; // skip notes already in the past

      const freq = midiToFreq(note.pitch);
      const velocity = note.velocity / 127;
      const noteGain = config.gain * velocity * 0.6; // scale down to avoid clipping

      // Oscillator
      const osc = ctx.createOscillator();
      osc.type = config.oscillatorType;
      osc.frequency.setValueAtTime(freq, startTime);

      // Filter per note
      const filter = ctx.createBiquadFilter();
      filter.type = config.filterType;
      filter.frequency.setValueAtTime(config.filterFreq, startTime);
      filter.Q.setValueAtTime(config.filterQ, startTime);

      // Gain envelope
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(noteGain, startTime + config.attack);
      gain.gain.setValueAtTime(noteGain, startTime + config.attack + config.decay * 0.3);
      gain.gain.linearRampToValueAtTime(noteGain * config.sustain, startTime + config.attack + config.decay);
      gain.gain.setValueAtTime(0, endTime);
      gain.gain.linearRampToValueAtTime(0, endTime + config.release * 0.5);

      // Connect: osc → filter → gain → trackGain (+ reverb)
      osc.connect(filter);
      filter.connect(gain);
      gain.connect(trackGain);
      if (reverbNode) {
        gain.connect(reverbNode);
      }

      osc.start(startTime);
      osc.stop(endTime + config.release);

      activeNodes.push({ osc, gain, filter });
    }
  }

  const totalDuration = maxEndTime - now;

  // Auto-stop
  timeoutId = setTimeout(() => {
    if (!playing) return;
    stopAll();
    if (loop) {
      // Restart playback
      loopTimeoutId = setTimeout(() => {
        if (playing) {
          // Recursive play — but we need to be careful about memory.
          // For simplicity, just stop here; caller handles loop via event.
        }
      }, 100);
    }
  }, (totalDuration + 0.5) * 1000);

  function stopAll() {
    playing = false;
    if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
    if (loopTimeoutId) { clearTimeout(loopTimeoutId); loopTimeoutId = null; }
    // Disconnect all nodes
    for (const node of activeNodes) {
      try {
        node.osc.stop();
        node.osc.disconnect();
        node.gain.disconnect();
        node.filter.disconnect();
      } catch { /* already stopped */ }
    }
    activeNodes.length = 0;
    trackGain.disconnect();
    dryGain.disconnect();
    wetGain.disconnect();
    if (reverbNode) reverbNode.disconnect();
  }

  // Handle loop via scheduling a restart timer
  if (loop) {
    const loopDuration = (totalDuration + 0.5) * 1000;
    loopTimeoutId = setTimeout(() => {
      if (!playing) return;
      stopAll();
      // Simple re-trigger: we don't recursively call playMidi to avoid memory leaks.
      // Instead, we rely on the caller (AudioEngine) to re-trigger via onEnd callback.
    }, loopDuration);
  }

  return {
    stop: stopAll,
    isPlaying: () => playing,
  };
}

/**
 * Create a simple impulse response for reverb.
 */
function createReverbImpulse(ctx: AudioContext, duration: number): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const length = Math.floor(sampleRate * duration);
  const buffer = ctx.createBuffer(2, length, sampleRate);

  for (let ch = 0; ch < 2; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      // Exponential decay
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (sampleRate * 0.1));
    }
  }
  return buffer;
}
